import { handleFormError } from '../../../utils/handleFormError'
import { TextInput } from '../../../components/FormUtils/TextInput'
import { useForm } from 'react-hook-form'
import { useAuth } from 'context/authentication/useAuth'
import to from 'await-to-js'
import { useState } from 'react'
import FbButton from '../../../components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { HiOutlineMail } from 'react-icons/hi'
import { useCooldownTimer } from 'hooks/useCooldownTimer'

const resetPasswordFormShape = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  non_field_errors: z.string().optional(),
})

function ResetPassword() {
  const { resetPassword } = useAuth()
  const [emailSent, setEmailSent] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const { cooldownSeconds, startCooldown, isOnCooldown } = useCooldownTimer()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
    getValues,
  } = useForm({
    defaultValues: {
      email: '',
      non_field_errors: '',
    },
    resolver: zodResolver(resetPasswordFormShape),
  })

  const sendResetEmail = async (email: string) => {
    const [err, res] = await to(resetPassword(email))
    if (err) {
      handleFormError(err, setError)
      return false
    }
    if (res) {
      setSubmittedEmail(email)
      setEmailSent(true)
      startCooldown()
      return true
    }
    return false
  }

  const onSubmit = handleSubmit(
    async (values) => {
      await sendResetEmail(values.email)
    },
    (errors) => console.warn(errors)
  )

  const handleResend = async () => {
    await sendResetEmail(submittedEmail || getValues().email)
  }

  // Success state - email has been sent
  if (emailSent) {
    return (
      <div className="w-96 flex-shrink-0">
        <div className="text-2xl font-light text-fg-secondary">
          Welcome to{' '}
          <span className="text-brand font-semibold">First Bite</span>
        </div>

        <div className="mt-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-surface-hover flex items-center justify-center mb-4">
            <HiOutlineMail className="w-8 h-8 text-brand" />
          </div>

          <h1 className="text-xl font-semibold mb-2">Check your inbox</h1>

          <p className="text-gray-600 mb-6">
            We&apos;ve sent password reset instructions to your email address.
          </p>

          <div className="w-full p-4 bg-gray-50 rounded-lg mb-6">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Tip:</span> If you don&apos;t see
              the email, check your spam or junk folder.
            </p>
          </div>

          <div className="flex flex-col gap-3 w-full">
            <FbButton
              variant="secondary"
              onClick={handleResend}
              disabled={isOnCooldown}
              className="w-full"
            >
              {isOnCooldown
                ? `Resend available in ${cooldownSeconds}s`
                : "Didn't receive it? Resend"}
            </FbButton>

            <FbLink to="/login" className="text-center">
              Back to login
            </FbLink>
          </div>
        </div>
      </div>
    )
  }

  // Initial state - email input form
  return (
    <div className="w-96 flex-shrink-0">
      <div className="text-2xl font-light text-fg-secondary">
        Welcome to <span className="text-brand font-semibold">First Bite</span>
      </div>
      <h1 className="text-lg mb-2 mt-4">Reset Password</h1>
      <p className="text-gray-600 mb-4">
        Enter your email and we&apos;ll send you instructions to reset your
        password.
      </p>
      <TextInput control={control} name="email" label="Email" />
      <FbButton
        type="submit"
        onClick={onSubmit}
        loading={isSubmitting}
        className="w-full mt-4"
      >
        Send Reset Link
      </FbButton>
      <div className="mt-4 text-center">
        <FbLink to="/login">Back to login</FbLink>
      </div>
    </div>
  )
}

export default ResetPassword
